import React, { useState } from 'react'
import { withStyles } from '@mui/styles'
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box } from '@mui/material'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { foldingPanelStyles } from './folding-panel.styles'

/**
 * @typedef {object} FoldingPanelComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [isExpanded=false] - Estado inicial del panel (expandido o colapsado).
 * @property {boolean} [noPadding] - Si es true, elimina el padding del contenido del panel.
 * @property {string} title - El título que se muestra en el encabezado del panel.
 * @property {React.ReactNode} [headerComponent] - Componente adicional que se renderiza en el encabezado junto al título.
 * @property {React.ReactNode} children - El contenido que se muestra cuando el panel está expandido.
 */

/**
 * Un componente de panel plegable/expandible basado en Accordion de Material-UI.
 *
 * Proporciona un panel colapsable con un título y contenido que se puede mostrar u ocultar
 * haciendo clic en el encabezado. Incluye un icono de flecha que indica el estado del panel
 * y permite agregar componentes adicionales en el encabezado.
 *
 * @component
 * @example
 * const MyComponent = () => {
 *   return (
 *     <FoldingPanelComponent
 *       title="Información Adicional"
 *       isExpanded={false}
 *       headerComponent={<Badge badgeContent={5} color="primary"><MailIcon /></Badge>}
 *     >
 *       <Typography>
 *         Este es el contenido que se muestra cuando el panel está expandido.
 *       </Typography>
 *     </FoldingPanelComponent>
 *   );
 * };
 *
 * @param {FoldingPanelComponentProps} props - Las props del componente FoldingPanelComponent.
 */
export const FoldingPanelComponent = withStyles(foldingPanelStyles)(({
  classes,
  isExpanded = false,
  noPadding,
  ...props
}) => {
  const [expanded, setExpanded] = useState(isExpanded)

  return (
    <div className={classes.panel}>
      <Accordion
        className={classes.folding}
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <AccordionSummary
          className={classes.foldingHeader}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
            <Typography variant="h6" style={{ marginRight: '16px' }}>
              {props.title}
            </Typography>
            {props.headerComponent && props.headerComponent}
          </Box>
        </AccordionSummary>
        <AccordionDetails style={noPadding ? { padding: 0 } : {}}>
          {props.children}
        </AccordionDetails>
      </Accordion>
    </div>
  )
})
